import React, { useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid, List, Network, LineChart, Building2, Plus, Sparkles, X,
} from "lucide-react";
import { useStore } from "../store";

const items = [
  { to: "/", end: true, icon: LayoutGrid, label: "Dashboard" },
  { to: "/incidents", icon: List, label: "Incidents" },
  { to: "/chain", icon: Network, label: "Chain" },
  { to: "/analytics", icon: LineChart, label: "Analytics" },
  { to: "/tenants", icon: Building2, label: "Tenants" },
];

export const BottomNav = () => {
  const openModal = useStore((s) => s.openModal);
  const isSidebarOpen = useStore((s) => s.isSidebarOpen);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);

  const beginPress = (e) => {
    longPressed.current = false;
    pressTimer.current = window.setTimeout(() => {
      longPressed.current = true;
      setMenuOpen(true);
    }, 480);
  };
  const endPress = () => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const handleClick = () => {
    // If it was a long-press, the menu opens — don't also run the quick-entry
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    openModal();
  };

  return (
    <>
      <nav
        className={
          "sm:hidden fixed bottom-0 left-0 right-0 z-[260] justify-around items-center safe-bottom " +
          (isSidebarOpen ? "hidden" : "flex")
        }
        style={{
          height: 60,
          background: "#0d0f18",
          borderTop: "1px solid rgba(255,255,255,0.12)",
        }}
        data-testid="bottom-nav"
      >
        {items.slice(0, 2).map((it) => (
          <NavLink
            key={it.label}
            to={it.to}
            end={it.end}
            className="flex flex-col items-center justify-center flex-1 h-full"
            style={({ isActive }) => ({
              color: isActive ? "#B5D4F4" : "#9198b0",
              gap: 3,
            })}
            data-testid={`bottom-nav-${it.label.toLowerCase()}`}
          >
            <it.icon size={18} strokeWidth={1.8} />
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.8px" }}>
              {it.label}
            </span>
          </NavLink>
        ))}

        {/* Quick-entry pinned center button — long-press for AI assistant menu */}
        <button
          onClick={handleClick}
          onMouseDown={beginPress}
          onMouseUp={endPress}
          onMouseLeave={endPress}
          onTouchStart={beginPress}
          onTouchEnd={endPress}
          onTouchCancel={endPress}
          onContextMenu={(e) => {
            e.preventDefault();
            longPressed.current = true;
            setMenuOpen(true);
          }}
          className="flex flex-col items-center justify-center flex-1 h-full"
          style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
          data-testid="bottom-nav-new"
          aria-label="New incident — tap quick entry, long-press for options"
        >
          <span
            className="flex items-center justify-center rounded-full"
            style={{
              width: 38,
              height: 38,
              background: "#185FA5",
              border: "1px solid #185FA5",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(24,95,165,0.45)",
              marginTop: -8,
            }}
          >
            <Plus size={18} />
          </span>
          <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9ec3ec", letterSpacing: "0.8px", marginTop: 2 }}>
            New
          </span>
        </button>

        {items.slice(2).map((it) => (
          <NavLink
            key={it.label}
            to={it.to}
            end={it.end}
            className="flex flex-col items-center justify-center flex-1 h-full"
            style={({ isActive }) => ({
              color: isActive ? "#B5D4F4" : "#9198b0",
              gap: 3,
            })}
            data-testid={`bottom-nav-${it.label.toLowerCase()}`}
          >
            <it.icon size={18} strokeWidth={1.8} />
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, letterSpacing: "0.8px" }}>
              {it.label}
            </span>
          </NavLink>
        ))}
      </nav>

      {menuOpen && (
        <NewMenuSheet
          onPick={(kind) => {
            setMenuOpen(false);
            if (kind === "quick") {
              openModal();
            } else if (kind === "ai") {
              navigate("/incidents/new/chat");
            }
          }}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </>
  );
};

const NewMenuSheet = ({ onPick, onClose }) => (
  <>
    <div
      className="sm:hidden fixed inset-0 z-[300]"
      style={{ background: "rgba(8,9,13,0.65)" }}
      onClick={onClose}
    />
    <div
      className="sm:hidden fixed left-0 right-0 z-[301] rounded-t-xl"
      style={{
        bottom: 0,
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.10)",
        borderBottom: "none",
        padding: "20px 16px 24px",
        boxShadow: "0 -8px 32px rgba(0,0,0,0.6)",
      }}
      data-testid="bottom-nav-new-menu"
    >
      <div
        className="mx-auto rounded-full mb-4"
        style={{ width: 40, height: 4, background: "rgba(255,255,255,0.15)" }}
      />
      <div className="flex justify-between items-center mb-3">
        <div
          style={{
            fontFamily: "Syne, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            color: "#f0f2f8",
          }}
        >
          New incident
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded"
          style={{ background: "transparent", color: "#9198b0", border: "none" }}
          data-testid="bottom-nav-new-menu-close"
        >
          <X size={14} />
        </button>
      </div>
      <button
        onClick={() => onPick("quick")}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-md mb-2"
        style={{
          background: "#0a0c12",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "#f0f2f8",
          fontFamily: "'DM Mono', monospace",
          fontSize: 13,
          cursor: "pointer",
        }}
        data-testid="bottom-nav-pick-quick"
      >
        <Plus size={16} style={{ color: "#9ec3ec" }} />
        <div style={{ textAlign: "left" }}>
          <div style={{ color: "#f0f2f8" }}>Quick entry</div>
          <div style={{ color: "#9198b0", fontSize: 10, marginTop: 2 }}>
            Template + title + severity
          </div>
        </div>
      </button>
      <button
        onClick={() => onPick("ai")}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-md"
        style={{
          background: "rgba(155,89,182,0.10)",
          border: "1px solid rgba(155,89,182,0.45)",
          color: "#c89adb",
          fontFamily: "'DM Mono', monospace",
          fontSize: 13,
          cursor: "pointer",
        }}
        data-testid="bottom-nav-pick-ai"
      >
        <Sparkles size={16} style={{ color: "#c89adb" }} />
        <div style={{ textAlign: "left" }}>
          <div style={{ color: "#c89adb" }}>AI Assistant</div>
          <div style={{ color: "#9198b0", fontSize: 10, marginTop: 2 }}>
            Describe it in natural language
          </div>
        </div>
      </button>
    </div>
  </>
);
